import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { encryptString, decryptString } from '../utils/crypto'

/** Parsed room token payload - either legacy roomId or stable customRoomId|companyId format */
export interface ParsedRoomToken {
  roomId?: string
  customRoomId?: string
  companyId?: string | null
}

interface JoinState {
  encryptedRoomToken: string | null
  setEncryptedToken: (token: string | null) => void
  createTokenForRoom: (roomId: string, options?: { customRoomId?: string; companyId?: string | null }) => Promise<string>
  decryptToken: (token: string) => Promise<string | null>
  decryptAndParseToken: (token: string) => Promise<ParsedRoomToken | null>
  clear: () => void
}

const STABLE_PREFIX = 'stable:'
const STABLE_SEPARATOR = '|'

export const useJoinStore = create<JoinState>()(
  subscribeWithSelector((set) => ({
    encryptedRoomToken: null,

    setEncryptedToken: (token: string | null) => set({ encryptedRoomToken: token }),

    createTokenForRoom: async (roomId: string, options?: { customRoomId?: string; companyId?: string | null }) => {
      let payload: string
      const hasStableId = options?.customRoomId !== undefined && options?.customRoomId !== ''
      if (hasStableId) {
        payload = `${STABLE_PREFIX}${options!.customRoomId}${STABLE_SEPARATOR}${options?.companyId ?? ''}`
      } else {
        payload = roomId
      }
      const token = await encryptString(payload)
      set({ encryptedRoomToken: token })
      return token
    },

    decryptToken: async (token: string) => {
      try {
        const decrypted = await decryptString(token)
        if (!decrypted) return null
        if (decrypted.startsWith(STABLE_PREFIX)) {
          const rest = decrypted.slice(STABLE_PREFIX.length)
          const [customRoomId, companyId] = rest.split(STABLE_SEPARATOR)
          return customRoomId || null
        }
        return decrypted
      } catch (err) {
        console.error('[JoinStore] Failed to decrypt token', err)
        return null
      }
    },

    decryptAndParseToken: async (token: string) => {
      try {
        const decrypted = await decryptString(token)
        if (!decrypted) return null
        if (decrypted.startsWith(STABLE_PREFIX)) {
          const rest = decrypted.slice(STABLE_PREFIX.length)
          const [customRoomId, companyId] = rest.split(STABLE_SEPARATOR)
          return {
            customRoomId: customRoomId || undefined,
            companyId: companyId ?? null,
          }
        }
        return { roomId: decrypted }
      } catch (err) {
        console.error('[JoinStore] Failed to decrypt token', err)
        return null
      }
    },

    clear: () => set({ encryptedRoomToken: null }),
  }))
)

export const createJoinLink = async (
  roomId: string,
  options?: { customRoomId?: string; companyId?: string | null }
): Promise<string> => {
  const token = await useJoinStore.getState().createTokenForRoom(roomId, {
    customRoomId: options?.customRoomId,
    companyId: options?.companyId ?? null,
  })
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  return `${origin}/join?rid=${token}`
}


